import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Box, ToggleButton, ToggleButtonGroup, Typography, Divider, Paper } from '@mui/material'
import { Instrument, Song, SONG_TYPES } from '../../../utils/types'
import { formatString } from '../../../utils/formatter'
import { useChangeCurrentSong } from '../../../hooks/contexts/currentSongContext'
import { PolyglotContext } from '../../../polyglot/polyGlotContext'

type Props = { songList?: Song[]; selectedInstrument: Instrument; hasChangedSoundCloseDrawer?: any }

export const toggleButtonGroupStyling = {
    backgroundColor: '#f5f5f5',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexWrap: 'wrap'
} as const

// NOTE: key change here:
// - Selected styles are more specific and also applied on hover
// - Hover effect only on devices that actually support hover (not touch)
// - Ripple disabled in component props
export const toggleButtonStyling = {
    color: '#555',
    backgroundColor: '#fff',
    borderRadius: '5px',
    margin: '0 0px',
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 500,
    padding: '4px 8px',

    // Selected should dominate (including while hovered)
    '&.Mui-selected': (theme: any) => ({
        backgroundColor: theme.palette.primary.main,
        color: '#fff'
    }),
    '&.Mui-selected:hover': (theme: any) => ({
        backgroundColor: theme.palette.primary.main,
        color: '#fff'
    }),

    // Only show grey hover on non-touch devices
    '@media (hover: hover) and (pointer: fine)': {
        '&:hover': {
            backgroundColor: '#e0e0e0'
        }
    }
} as const

const AllSoundsAvailable = ({ songList, selectedInstrument, hasChangedSoundCloseDrawer }: Props) => {
    const { polyglot } = useContext(PolyglotContext)
    const changeCurrentSong = useChangeCurrentSong()
    const instrumentId = selectedInstrument?.deviceId

    // map: instrumentId -> songId
    const [instrumentSong, setInstrumentSong] = useState<Record<string, string>>({})

    const currentSelection = instrumentId ? (instrumentSong[instrumentId] ?? '') : ''

    const selectSong = (songId: string) => {
        if (!instrumentId) return
        if (instrumentSong[instrumentId] === songId) return // dedupe: don’t resend
        setInstrumentSong((prev) => ({ ...prev, [instrumentId]: songId }))
        changeCurrentSong({ songId, deviceId: instrumentId })
        hasChangedSoundCloseDrawer()
    }

    // Rehydrate once
    useEffect(() => {
        try {
            const stored = localStorage.getItem('instrumentSong')
            if (stored) setInstrumentSong(JSON.parse(stored))
        } catch {}
    }, [])

    // Persist on change
    useEffect(() => {
        localStorage.setItem('instrumentSong', JSON.stringify(instrumentSong))
    }, [instrumentSong])

    // Partition + sort songs by type
    const { music, soundbanks, keySongs } = useMemo(() => {
        const byType = {
            music: [] as Song[],
            soundbanks: [] as Song[],
            keySongs: [] as Song[]
        }
        ;(songList ?? []).forEach((s) => {
            if (s.type === SONG_TYPES.MUSIC) byType.music.push(s)
            else if (s.type === SONG_TYPES.SOUNDBANKS) byType.soundbanks.push(s)
            else if (s.type === SONG_TYPES.KEY) byType.keySongs.push(s)
        })
        const sortByTitle = (a: Song, b: Song) => a.title.localeCompare(b.title, 'sv')
        byType.music.sort(sortByTitle)
        byType.soundbanks.sort(sortByTitle)
        byType.keySongs.sort(sortByTitle)
        return byType
    }, [songList])

    const renderSection = (title: string, items: Song[]) => {
        if (!items.length) return null
        return (
            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: '#fafafa' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, textAlign: 'center' }}>
                    {title}
                </Typography>

                <ToggleButtonGroup
                    value={currentSelection}
                    exclusive
                    fullWidth
                    aria-label={title}
                    sx={{
                        ...toggleButtonGroupStyling,
                        gap: 1,
                        p: 1,
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-start'
                    }}
                >
                    {items.map((s) => (
                        <ToggleButton
                            key={s.id}
                            value={s.id}
                            aria-label={s.title}
                            type="button"
                            disableRipple
                            disableFocusRipple
                            disableTouchRipple
                            onClick={() => selectSong(s.id)} // instant select (mobile + desktop)
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    selectSong(s.id)
                                }
                            }}
                            sx={{
                                ...toggleButtonStyling,
                                border: '1px solid #e0e0e0',
                                borderRadius: 20, // pill look reads better on mobile
                                px: 1.25,
                                py: 0.5
                            }}
                        >
                            {formatString(s.title)}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Paper>
        )
    }

    if (!songList?.length) return null

    return (
        <Box sx={{ display: 'grid', gap: 2 }}>
            {renderSection(polyglot.t('home.instrumentInformation.paragraphMusic'), music)}
            {music.length && (soundbanks.length || keySongs.length) ? <Divider /> : null}
            {renderSection(polyglot.t('home.instrumentInformation.paragraphSoundscape'), soundbanks)}
            {soundbanks.length && keySongs.length ? <Divider /> : null}
            {renderSection(polyglot.t('home.instrumentInformation.paragraphKey'), keySongs)}
        </Box>
    )
}

export default AllSoundsAvailable
